import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Shield, Zap, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Iframe-friendly embeddable signup widget.
 *
 * URL format:
 *   /embed/signup?api_key=nks_live_XXX&partner_slug=kcb-bank&lang=en
 *
 * Posts to /api/public/embed/signup with X-API-Key header and returns
 * access_token + user payload. Designed to be embedded inside partner
 * web/mobile apps via an iframe.
 *
 * postMessage events fired to window.parent:
 *  - { type: "nyoksure:signup:success", access_token, user }
 *  - { type: "nyoksure:signup:error", error }
 */
export default function EmbedSignup() {
  const [params] = useSearchParams();
  const apiKey = params.get("api_key") || "";
  const partnerSlug = params.get("partner_slug") || "";

  const [form, setForm] = useState({
    business_name: "", owner_name: "", phone: "+254",
    email: "", password: "", country: "KE",
    business_type: "retail", language: "en",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  // Notify parent that the widget is mounted (so it can resize the iframe etc.)
  useEffect(() => {
    try {
      window.parent?.postMessage({ type: "nyoksure:embed:mounted" }, "*");
    } catch (_) { /* ignore */ }
  }, []);

  function update(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!apiKey) {
      setErr("Missing api_key in URL.");
      return;
    }
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (partnerSlug) payload.partner_slug = partnerSlug;
      const { data } = await axios.post(
        `${BACKEND}/api/public/embed/signup`,
        payload,
        { headers: { "X-API-Key": apiKey, "Content-Type": "application/json" } },
      );
      setSuccess(data);
      try {
        window.parent?.postMessage(
          {
            type: "nyoksure:signup:success",
            access_token: data.access_token,
            user: data.user,
            merchant_id: data.user?.merchant_id,
          },
          "*",
        );
      } catch (_) { /* ignore */ }
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : (detail || e2.message || "Sign-up failed");
      setErr(msg);
      try {
        window.parent?.postMessage({ type: "nyoksure:signup:error", error: msg }, "*");
      } catch (_) { /* ignore */ }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#f4f2eb] flex items-center justify-center px-6" data-testid="embed-success">
        <div className="bg-white border border-[#114232]/15 rounded-md p-8 max-w-md w-full text-center space-y-3">
          <CheckCircle2 className="w-10 h-10 text-[#114232] mx-auto" />
          <h2 className="font-heading text-xl font-bold text-[#114232]">You're covered</h2>
          <p className="text-sm text-[#5c6360]">
            {success.user?.name}, your merchant account is created. Your starter Merchant-Lite policy is ready to activate after the first premium payment.
          </p>
          <div className="text-[11px] font-mono text-[#5c6360] bg-stone-50 rounded p-2 break-all">
            Merchant ID: {success.user?.merchant_id}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f2eb] px-6 py-8 flex items-center justify-center" data-testid="embed-signup-page">
      <form
        onSubmit={onSubmit}
        className="bg-white border border-[#114232]/15 rounded-md p-6 max-w-md w-full space-y-4"
        data-testid="embed-signup-form"
      >
        <div className="flex items-center gap-2 mb-1">
          <div className="relative">
            <Shield className="w-6 h-6 text-[#114232]" strokeWidth={1.5} />
            <Zap className="w-3 h-3 text-[#c15337] absolute -bottom-0.5 -right-0.5" fill="#c15337" strokeWidth={0} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#c15337]">
            Powered by Nyoksure
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#114232]">Get cyber-cover for your shop</h1>
        <p className="text-xs text-[#5c6360]">Takes 60 seconds. First week pays KES 99.</p>

        {!apiKey && (
          <div className="text-xs text-[#8b3a23] bg-[#c15337]/10 border border-[#c15337]/25 rounded p-2" data-testid="embed-no-key-warning">
            This widget requires <code>?api_key=…</code> in the URL.
          </div>
        )}

        {[
          ["business_name", "Business name", "text"],
          ["owner_name", "Your name", "text"],
          ["phone", "Phone (M-Pesa)", "tel"],
          ["email", "Email", "email"],
          ["password", "Password (≥8 chars)", "password"],
        ].map(([k, label, type]) => (
          <div key={k}>
            <label className="block text-[11px] uppercase tracking-wider text-[#5c6360] font-bold mb-1">{label}</label>
            <input
              type={type}
              value={form[k]}
              onChange={(e) => update(k, e.target.value)}
              required
              data-testid={`embed-input-${k}`}
              className="w-full border border-[#114232]/20 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#c15337]"
            />
          </div>
        ))}

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[#5c6360] font-bold mb-1">Business type</label>
          <select
            value={form.business_type}
            onChange={(e) => update("business_type", e.target.value)}
            data-testid="embed-input-business_type"
            className="w-full border border-[#114232]/20 rounded px-3 py-2 text-sm bg-white"
          >
            <option value="retail">Retail / shop</option>
            <option value="food">Food / restaurant</option>
            <option value="services">Services</option>
            <option value="other">Other</option>
          </select>
        </div>

        {err && (
          <div className="text-xs text-[#8b3a23] bg-[#c15337]/10 border border-[#c15337]/25 rounded p-2" data-testid="embed-error">{err}</div>
        )}

        <button
          type="submit"
          disabled={submitting || !apiKey}
          data-testid="embed-submit-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-md bg-[#c15337] text-white text-sm font-semibold hover:opacity-95 disabled:opacity-50"
        >
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {submitting ? "Creating account…" : "Get cover now"}
        </button>

        <div className="text-[10px] text-[#5c6360] text-center">
          By signing up you accept the Key Facts & cooling-off terms.
        </div>
      </form>
    </div>
  );
}
